import React from 'react';
import {
  Button, Checkbox, FormControlLabel, SelectChangeEvent, TextField, Typography,
  Box,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import AlertDialog from '../AlertDialog';
import { getBanks, postBankAccount } from '../../queries/account';
import { getBanksResponse } from '../../types/account';
import BasicSelect from '../BasicSelect';

type AddBankAccountDialogProps = {
    token: string,
    open?: boolean,
    closeTrigger?: boolean,
    onSuccess?: () => void,
    isLoanRequest?: boolean
}

const defaultProps = {
  open: false,
  closeTrigger: false,
  onSuccess: undefined,
  isLoanRequest: false,
};

export default function AddBankAccountDialog(props:AddBankAccountDialogProps) {
  const {
    token,
    open = defaultProps.open,
    closeTrigger = defaultProps.closeTrigger,
    onSuccess = defaultProps.onSuccess,
    isLoanRequest = defaultProps.isLoanRequest,
  } = props;
  const [banks, setBanks] = React.useState<getBanksResponse['data']['banks']>([]);
  const [bankId, setBankId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [accountNumber, setAccountNumber] = React.useState('');
  const [accountType, setAccountType] = React.useState<string>('');
  const [checked, setChecked] = React.useState(isLoanRequest);
  const [error, setError] = React.useState(false);
  const isAccountNumberError = /[^1234567890-]/.test(accountNumber);
  const disableSubmitButton = !accountNumber.replaceAll('-', '') || !bankId || !accountType || !checked;
  const checkAccount = (account: string) => {
    setAccountNumber(account);
  };
  const submitBankAccount = async () => {
    try {
      setLoading(true);
      await postBankAccount({ accountNumber: accountNumber.replaceAll('-', ''), bankId, accountType }, token);
      setTimeout(() => {
        setLoading(false);
        if (onSuccess) {
          onSuccess();
        } else {
          window.location.reload();
        }
      }, 1500);
    } catch (e) {
      setLoading(false);
      setError(true);
    }
  };

  const getBankNamesAndIds = async () => {
    setLoading(true);
    const banksResponse = await getBanks(token);
    setBanks(banksResponse.data.banks);
    setLoading(false);
  };

  React.useEffect(() => {
    getBankNamesAndIds();
  }, []);

  const buttonSubmitLabel = isLoanRequest ? 'Finalizar' : 'Agregar';
  const buttonDialog = !isLoanRequest
    ? <Button variant="text" style={{ textDecoration: 'underline', width: 'max-content', paddingInline: '5%' }}>Agregar nueva cuenta bancaria</Button>
    : <div style={{ display: 'none' }} />;

  return (
    <AlertDialog
      closeButtonDisabled={loading}
      submitButtonDisabled={disableSubmitButton || loading}
      action={submitBankAccount}
      isTriggered={open}
      triggerClose={closeTrigger}
      dialogTitle={(
        <Typography align="center" variant="inherit" fontWeight="bold" style={{ marginBlock: 10 }} component="div">
          {isLoanRequest ? <>Ingresar cuenta bancaria</> : <>Agregar nueva cuenta bancaria</>}
        </Typography>
      )}
      maxWidth="xs"
      hideCloseButton={isLoanRequest}
      button={buttonDialog}
      dialogContent={(
        <div style={{
          marginTop: isLoanRequest ? 0 : 15,
          placeItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
        >
          {isLoanRequest && (
            <Box sx={{ textAlign: 'center', paddingInline: 5 }}>
              <Typography color="primary" variant="body2" gutterBottom>
                Necesitamos tus datos bancarios para transferirte el dinero de tu préstamo.
              </Typography>
            </Box>
          )}
          <Box sx={{ paddingInline: 5, width: '100%' }}>
            <Typography color="secondary" variant="body2">Banco</Typography>
            <BasicSelect
              onChange={(e: SelectChangeEvent<string>) => setBankId(e.target.value)}
              value={bankId}
              options={banks}
              optKey="name"
              selectId="bank"
              disabled={loading}
              width="100%"
            />
          </Box>
          <Box sx={{ paddingInline: 5, width: '100%' }}>
            <Typography color="secondary" variant="body2">Tipo de cuenta</Typography>
            <BasicSelect
              onChange={(e: SelectChangeEvent<string>) => setAccountType(e.target.value)}
              value={accountType}
              options={[{ id: 'corriente', name: 'Corriente' }, { id: 'vista', name: 'Vista / Rut' }, { id: 'ahorro', name: 'Ahorro' }]}
              optKey="name"
              selectId="accountType"
              disabled={loading}
              width="100%"
            />
          </Box>
          <Box sx={{ paddingInline: 5, width: '100%' }}>
            <Typography color="secondary" variant="body2">Número de cuenta</Typography>
            <NumericFormat
              id="bankNumber"
              customInput={TextField}
              size="small"
              thousandSeparator="-"
              value={accountNumber}
              fullWidth
              variant="outlined"
              onChange={(e) => checkAccount(e.target.value)}
              error={isAccountNumberError}
              helperText={isAccountNumberError ? 'Por favor ingrese un número de cuenta válido' : ''}
              disabled={loading}
              width="100%"
            />

          </Box>
          {!isLoanRequest && (
            <Box sx={{ paddingInline: 5, width: '100%' }}>
              <FormControlLabel
                style={{ marginTop: 10, width: '100%' }}
                control={(
                  <Checkbox
                    size="small"
                    disabled={loading}
                    checked={checked}
                    onClick={() => setChecked((o) => !o)}
                  />
              )}
                label={(
                  <Typography variant="caption">
                    Acepto reemplazar mi cuenta bancaria registrada anteriormente con esta
                  </Typography>
              )}
              />
            </Box>
          )}
          {error && <Typography color="error" variant="caption" component="div">Ha ocurrido un error. Por favor contáctanos.</Typography>}
        </div>
)}
      submitButtonLabel={loading ? 'Cargando...' : buttonSubmitLabel}
    />
  );
}

AddBankAccountDialog.defaultProps = defaultProps;
